import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { doc, getDoc, type DocumentData } from "firebase/firestore";
import { db, call } from "../firebase";
import { useSession } from "../session";
import { canApproveSite, canShare, canShareDownload } from "../domain";
import { friendly, toast } from "../errors";
import { ask, askText } from "../dialog";

interface ShareRow { token: string; url: string; recipient: string; allowDownload: boolean; revoked: boolean; views: number; expiresAt: number; lastViewedAt: number | null; createdByName: string }

/** DOC-08: tạo / thu hồi link chia sẻ có hạn cho người ngoài (đối tác, ngân hàng, cơ quan…). */
export default function Share() {
  const { docId = "" } = useParams();
  const { member } = useSession();
  const [d, setD] = useState<DocumentData | null>(null);
  const [rows, setRows] = useState<ShareRow[]>([]);
  const [f, setF] = useState({ recipient: "", days: 7, allowDownload: false });
  const [busy, setBusy] = useState(false);
  const [made, setMade] = useState<string | null>(null);

  const load = () => call<object, { shares: ShareRow[] }>("listShares")({ docId }).then((r) => setRows(r.shares)).catch((e) => toast(friendly(e)));
  useEffect(() => {
    getDoc(doc(db, "documents", docId)).then((s) => setD(s.data() ?? null)).catch(() => undefined);
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docId]);

  if (!member) return null;
  // L53: chưa đọc xong tài liệu thì chưa hiện form (tránh báo nhầm "chỉ cho xem")
  if (!d) return <><h1>Chia sẻ ra ngoài</h1><p className="muted">Đang tải…</p></>;
  if (!canShare(member, d)) return <><h1>Chia sẻ tài liệu</h1><div className="card">Bạn không có quyền chia sẻ tài liệu này ra ngoài.</div></>;

  const create = async () => {
    if (!f.recipient.trim()) return toast("Ghi tên người nhận / đơn vị nhận để Hub in lên dấu mờ và ghi nhật ký.");
    setBusy(true);
    try {
      const r = await call<object, { url: string }>("createShare")({ docId, days: f.days, allowDownload: f.allowDownload, recipient: f.recipient.trim() });
      setMade(r.url); setF({ ...f, recipient: "" });
      try { await navigator.clipboard.writeText(r.url); toast("Đã tạo link và chép vào bộ nhớ tạm.", "ok"); } catch { toast("Đã tạo link.", "ok"); }
      void load();
    } catch (e) { toast(friendly(e)); }
    setBusy(false);
  };
  const revoke = async (t: string) => {
    if (!await ask({ title: "Thu hồi link chia sẻ?", danger: true, confirm: "Thu hồi",
      message: "Người nhận sẽ không mở được link này nữa. Muốn chia sẻ lại thì tạo link mới." })) return;
    try { await call("revokeShare")({ token: t }); toast("Đã thu hồi link.", "ok"); void load(); } catch (e) { toast(friendly(e)); }
  };
  const copy = async (u: string) => { try { await navigator.clipboard.writeText(u); toast("Đã chép link.", "ok"); } catch { await askText({ title: "Chép link chia sẻ", hideCancel: true, confirm: "Xong", input: { label: "Link", value: u, readOnly: true },
    message: "Trình duyệt không cho chép tự động. Giữ ngón tay (hoặc Ctrl/Cmd + C) để chép link bên dưới." }); } };

  const now = Date.now();
  return (
    <>
      <h1>Chia sẻ ra ngoài</h1>
      <p className="sub">{d ? <><span className="code">{d.code}</span> · {d.title}</> : "…"}</p>
      <div className="card" style={{ display: "flex", flexDirection: "column", gap: 14, maxWidth: 760, marginBottom: 16 }}>
        <div className="grid">
          <div className="field"><label htmlFor="rc">Người / đơn vị nhận *</label>
            <input id="rc" value={f.recipient} onChange={(e) => setF({ ...f, recipient: e.target.value })} placeholder="vd: Ngân hàng ACB – chị Lan" /></div>
          <div className="field"><label htmlFor="dy">Thời hạn link</label>
            <select id="dy" value={f.days} onChange={(e) => setF({ ...f, days: Number(e.target.value) })}>
              {[1, 3, 7, 30].map((n) => <option key={n} value={n}>{n} ngày</option>)}</select></div>
        </div>
        {d && canShareDownload(member, d)
          ? <div className="checks"><label><input type="checkbox" checked={f.allowDownload} onChange={(e) => setF({ ...f, allowDownload: e.target.checked })} />Cho phép tải về file gốc (mặc định: chỉ xem, người nhận không lấy được file gốc)</label></div>
          : <p className="muted" style={{ margin: 0 }}>Link chỉ cho xem. Cho phép tải về file gốc với loại tài liệu này cần Quản trị, Ban giám đốc hoặc Quản lý chung.</p>}
        <p className="muted" style={{ margin: 0 }}>Người nhận mở link không cần tài khoản. Trang xem có dấu mờ ghi tên người nhận; mỗi lượt mở được ghi nhật ký. Link chỉ mở đúng bản đã duyệt hiện tại.</p>
        <div className="row" style={{ justifyContent: "flex-end" }}>
          <Link className="btn" style={{ display: "flex", alignItems: "center", textDecoration: "none" }} to="/documents">Về Kho tài liệu</Link>
          <button className="btn primary" disabled={busy} onClick={create}>Tạo link chia sẻ</button>
        </div>
        {made && <div className="ok" style={{ wordBreak: "break-all" }}>Link mới: <b>{made}</b></div>}
      </div>

      <h2 className="grpH g-ok">Các link đã tạo <span>· {rows.length}</span></h2>
      <div className="card tableWrap" style={{ padding: 0 }}>
        <table className="table stack">
          <thead><tr><th>Người nhận</th><th>Hết hạn</th><th>Quyền</th><th>Lượt mở</th><th>Trạng thái</th><th></th></tr></thead>
          <tbody>
            {rows.map((r) => {
              const live = !r.revoked && r.expiresAt > now;
              return (
                <tr key={r.token}>
                  <td className="cardHead"><b>{r.recipient}</b><div className="muted">tạo bởi {r.createdByName}</div></td>
                  <td data-label="Hết hạn">{new Date(r.expiresAt).toLocaleString("vi-VN")}</td>
                  <td data-label="Quyền">{r.allowDownload ? "Xem + tải về" : "Chỉ xem"}</td>
                  <td data-label="Lượt mở">{r.views}{r.lastViewedAt ? <div className="muted">gần nhất {new Date(r.lastViewedAt).toLocaleString("vi-VN")}</div> : null}</td>
                  <td data-label="Trạng thái"><span className={`pill ${live ? "approved" : ""}`}>{r.revoked ? "Đã thu hồi" : live ? "Đang hiệu lực" : "Hết hạn"}</span></td>
                  <td className="actions"><div className="row-actions">
                    {live && <button className="btn" onClick={() => copy(r.url)}>Chép link</button>}
                    {live && (canApproveSite(member, d?.siteId ?? "") || r.createdByName === member.name) && <button className="btn" onClick={() => revoke(r.token)}>Thu hồi</button>}
                  </div></td>
                </tr>
              );
            })}
            {!rows.length && <tr><td colSpan={6} className="muted">Chưa có link chia sẻ nào.</td></tr>}
          </tbody>
        </table>
      </div>
    </>
  );
}
